/* eslint-disable */
import * as React from 'react';
import PropTypes from 'prop-types';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { useNavigate } from 'react-router-dom';
import { setAuthUser, setAuthToken } from 'src/redux/reducers/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { format, sub } from 'date-fns';
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { endpoints } from '../../config';
import { authApiCall } from '../../services/http';
import { AppWidgetSummary, AppCurrentVisits } from '../../sections/@dashboard/app';
import { fSecondsToHours } from '../../utils/formatTime';
import Iconify from '../../components/Iconify';
import MeetingStatusTooltip from '../../components/MeetingStatusTooltip';

const TOKEN_USAGE_FEATURES = ['chat', 'template_agent', 'blocknote_agent', 'opportunity_finder', 'report'];
const RANGE_OPTIONS = [
  { value: 7, label: 'Últimos 7 días' },
  { value: 14, label: 'Últimos 14 días' },
  { value: 30, label: 'Últimos 30 días' },
];

const formatUsd = (n) => `$${(n || 0).toFixed(4)}`;
const formatInt = (n) => (Number(n) || 0).toLocaleString('es-ES');

// Agrupa las stages devueltas por /nlp-api/token-usage por un campo (`stage` o `model`):
// nº de llamadas, tokens y coste total. Ordenado por coste desc.
// `collectField` opcional: recoge valores distintos del otro campo por grupo (p. ej. los
// modelos usados en cada stage).
function aggregateBy(rows, field, collectField) {
  const map = new Map();
  rows.forEach((r) => {
    const k = r?.[field] || '—';
    if (!map.has(k)) {
      map.set(k, { key: k, calls: 0, inputTokens: 0, outputTokens: 0, cost: 0, values: new Set() });
    }
    const a = map.get(k);
    a.calls += 1;
    a.inputTokens += Number(r?.inputTokens) || 0;
    a.outputTokens += Number(r?.outputTokens) || 0;
    a.cost += Number(r?.cost) || 0;
    if (collectField && r?.[collectField]) a.values.add(r[collectField]);
  });
  return [...map.values()].map((a) => ({ ...a, values: [...a.values] })).sort((a, b) => b.cost - a.cost);
}

UserMetricsDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  userId: PropTypes.number,
};

function UserMetricsDialog(props) {
  const { onClose, open, userId } = props;
  const [userData, setUserData] = React.useState(null);
  const [rangeDays, setRangeDays] = React.useState(7);
  const [tokenUsage, setTokenUsage] = React.useState(null);
  const [tokenUsageLoading, setTokenUsageLoading] = React.useState(false);
  const [expandedFeatures, setExpandedFeatures] = React.useState(() => new Set());
  const authToken = useSelector((state) => state.auth.authToken);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const authApiCallHookProps = {
    navigate,
    dispatch,
    setAuthUser,
    setAuthToken,
    authToken,
    redirectRoute: null,
  };

  const handleClose = () => {
    onClose();
  };

  React.useEffect(() => {
    (async () => {
      if (userId !== -1) {
        const result = await authApiCall({
          method: 'GET',
          endpoint: endpoints.kpis,
          path: `/${userId}`,
          ...authApiCallHookProps,
        });
        setUserData(result.data);
      }
    })();
  }, [userId]);

  React.useEffect(() => {
    if (userId === -1 || !open) return;

    let cancelled = false;
    setTokenUsageLoading(true);
    setTokenUsage(null);
    setExpandedFeatures(new Set());

    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(sub(new Date(), { days: rangeDays - 1 }), 'yyyy-MM-dd');

    (async () => {
      const results = await Promise.all(
        TOKEN_USAGE_FEATURES.map(async (sessionType) => {
          try {
            const res = await authApiCall({
              method: 'GET',
              endpoint: endpoints.tokenUsage,
              path: `?userId=${userId}&sessionType=${sessionType}&from=${from}&to=${to}`,
              ...authApiCallHookProps,
            });
            const stages = Array.isArray(res?.data?.stages) ? res.data.stages : [];
            return { sessionType, totalCost: res?.data?.totalCost || 0, stages };
          } catch (e) {
            return { sessionType, totalCost: 0, stages: [], error: true };
          }
        })
      );

      if (cancelled) return;
      setTokenUsage({ from, to, results });
      setTokenUsageLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, open, rangeDays]);

  const usedFeatures = tokenUsage ? tokenUsage.results.filter((r) => r.totalCost > 0) : [];
  const totalSpent = usedFeatures.reduce((acc, r) => acc + r.totalCost, 0);

  const toggleFeature = (sessionType) => {
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(sessionType)) next.delete(sessionType);
      else next.add(sessionType);
      return next;
    });
  };

  return (
    <Dialog fullWidth={true} maxWidth="md" onClose={handleClose} open={open}>
      {userData && (
        <Box p={2}>
          <DialogTitle align="left">
            Métricas para{' '}
            <Typography color="primary" variant="body2">
              {userData.userInfo.email}
            </Typography>
          </DialogTitle>

          <Grid pt={2} spacing={2} container>
            <Grid item xs={12} sm={6} md={6}>
              <Stack spacing={2}>
                <AppWidgetSummary
                  py={2}
                  color="secondary"
                  fm={fSecondsToHours}
                  title="Tiempo medio de reunión"
                  total={userData.meetings.averageDuration}
                  icon={'eva:clock-outline'}
                />
                <Card variant="outlined" sx={{ px: 2, py: 1.5, boxShadow: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Iconify
                      icon="eva:message-circle-outline"
                      width={20}
                      height={20}
                      sx={{ color: 'text.secondary' }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Reuniones totales
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Puede incluir reuniones eliminadas
                      </Typography>
                    </Box>
                    <MeetingStatusTooltip statusCounts={userData.meetings.statusCounts}>
                      <Typography
                        variant="subtitle1"
                        color="text.primary"
                        sx={{ ...(userData.meetings.statusCounts && { cursor: 'help' }) }}
                      >
                        {userData.meetings.total}
                      </Typography>
                    </MeetingStatusTooltip>
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <AppCurrentVisits
                title="Reuniones por plataforma"
                chartData={userData.meetings.totalByPlatform}
                chartColors={[
                  theme.palette.chart.red[0],
                  theme.palette.chart.blue[0],
                  theme.palette.chart.violet[0],
                  theme.palette.chart.green[0],
                ]}
              />
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ boxShadow: 0 }}>
                <CardHeader
                  title="Uso de features"
                  titleTypographyProps={{ variant: 'subtitle1' }}
                  subheader={
                    tokenUsage
                      ? `Rango: ${tokenUsage.from} → ${tokenUsage.to}`
                      : 'Calculando rango…'
                  }
                  subheaderTypographyProps={{ variant: 'caption' }}
                  action={
                    <TextField
                      select
                      size="small"
                      value={rangeDays}
                      onChange={(e) => setRangeDays(Number(e.target.value))}
                      sx={{ minWidth: 160 }}
                    >
                      {RANGE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />
                <Divider />
                <Box sx={{ p: 2 }}>
                  {tokenUsageLoading && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Cargando uso por feature…
                      </Typography>
                    </Stack>
                  )}

                  {!tokenUsageLoading && tokenUsage && usedFeatures.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      El usuario no ha utilizado ninguna feature en este rango.
                    </Typography>
                  )}

                  {!tokenUsageLoading && usedFeatures.length > 0 && (
                    <Stack spacing={1}>
                      {usedFeatures.map((r) => {
                        const open = expandedFeatures.has(r.sessionType);
                        const hasStages = Array.isArray(r.stages) && r.stages.length > 0;
                        const byStage = hasStages ? aggregateBy(r.stages, 'stage', 'model') : [];
                        const byModel = hasStages ? aggregateBy(r.stages, 'model') : [];
                        return (
                          <Box
                            key={r.sessionType}
                            sx={{
                              borderRadius: 1,
                              bgcolor: 'background.neutral',
                              overflow: 'hidden',
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              onClick={() => hasStages && toggleFeature(r.sessionType)}
                              sx={{
                                px: 1.5,
                                py: 1,
                                cursor: hasStages ? 'pointer' : 'default',
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  disabled={!hasStages}
                                  sx={{ visibility: hasStages ? 'visible' : 'hidden' }}
                                >
                                  <Iconify
                                    icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
                                    width={16}
                                    height={16}
                                  />
                                </IconButton>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {r.sessionType}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" color="text.primary">
                                {formatUsd(r.totalCost)}
                              </Typography>
                            </Stack>
                            {hasStages && (
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Coste por stage
                                  </Typography>
                                  <Table size="small" sx={{ mt: 0.5, mb: 1.5 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Stage</TableCell>
                                        <TableCell>Modelos</TableCell>
                                        <TableCell align="right">Llamadas</TableCell>
                                        <TableCell align="right">Tokens (in / out)</TableCell>
                                        <TableCell align="right">Coste</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {byStage.map((s) => (
                                        <TableRow key={s.key}>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>{s.key}</TableCell>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {s.values && s.values.length ? s.values.join(', ') : '—'}
                                          </TableCell>
                                          <TableCell align="right">{s.calls}</TableCell>
                                          <TableCell align="right">
                                            {formatInt(s.inputTokens)} / {formatInt(s.outputTokens)}
                                          </TableCell>
                                          <TableCell align="right">{formatUsd(s.cost)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                  <Typography variant="caption" color="text.secondary">
                                    Coste por modelo
                                  </Typography>
                                  <Table size="small" sx={{ mt: 0.5 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Modelo</TableCell>
                                        <TableCell align="right">Llamadas</TableCell>
                                        <TableCell align="right">Tokens (in / out)</TableCell>
                                        <TableCell align="right">Coste</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {byModel.map((m) => (
                                        <TableRow key={m.key}>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>{m.key}</TableCell>
                                          <TableCell align="right">{m.calls}</TableCell>
                                          <TableCell align="right">
                                            {formatInt(m.inputTokens)} / {formatInt(m.outputTokens)}
                                          </TableCell>
                                          <TableCell align="right">{formatUsd(m.cost)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            )}
                          </Box>
                        );
                      })}
                      <Divider />
                      <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, pt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="subtitle2">{formatUsd(totalSpent)}</Typography>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Dialog>
  );
}

export default UserMetricsDialog;
